import { useForm } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Category, Account } from '@/types'

interface NewTransactionFormProps {
  account: Account
  categories: Category[]
}

export function NewTransactionForm({ account, categories }: NewTransactionFormProps) {
  const { data, setData, post, processing, reset } = useForm({
    account_id: account.id,
    unique_reference: crypto.randomUUID(),
    date: new Date().toISOString().split('T')[0],
    memo: '',
    amount: '',
    amount_currency: account.currency,
    category_id: '',
  })

  const parsedAmount = parseFloat(data.amount)
  const filteredCategories = data.amount && !isNaN(parsedAmount)
    ? categories.filter((c) => {
        if (c.kind === 'transfer') return true
        if (parsedAmount >= 0) return c.kind === 'revenue'
        return c.kind === 'cost'
      })
    : categories

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/transactions', {
      preserveScroll: true,
      onSuccess: () => reset('memo', 'amount', 'category_id'),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mb-4">
      <div className="grid grid-cols-2 gap-2.5 md:flex md:items-end">
        <div>
          <Label className="text-[11px] font-medium text-muted-foreground">Date</Label>
          <Input
            type="date"
            value={data.date}
            onChange={(e) => setData('date', e.target.value)}
            className="h-8 text-[13px]"
          />
        </div>
        <div>
          <Label className="text-[11px] font-medium text-muted-foreground">Memo</Label>
          <Input
            value={data.memo}
            onChange={(e) => setData('memo', e.target.value)}
            className="h-8 text-[13px]"
          />
        </div>
        <div>
          <Label className="text-[11px] font-medium text-muted-foreground">Amount</Label>
          <Input
            value={data.amount}
            onChange={(e) => setData('amount', e.target.value)}
            className="h-8 text-[13px]"
          />
        </div>
        <div>
          <Label className="text-[11px] font-medium text-muted-foreground">Category</Label>
          <Select
            value={data.category_id || '__none__'}
            onValueChange={(value) => setData('category_id', value === '__none__' ? '' : value)}
          >
            <SelectTrigger className="h-8 text-[13px]">
              <SelectValue placeholder="Uncategorized" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Uncategorized</SelectItem>
              {filteredCategories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <button type="submit" disabled={processing} className="hidden">Add</button>
    </form>
  )
}
